import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  type DeployIdentityScopeMigrationRequest,
  type DeployIdentityScopeMigrationResult,
  type ProjectSummary,
  type ScopeMigrationIssue,
  useRunDeployIdentityScopeMigration,
} from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";

type Environment = NonNullable<DeployIdentityScopeMigrationRequest["adopt"]>[number];
type ScopeRun = ReturnType<typeof useRunDeployIdentityScopeMigration>;

const STATE_LABEL: Record<DeployIdentityScopeMigrationResult["state"], string> = {
  ready: "Ready",
  blocked: "Blocked",
  unchanged: "Nothing to do",
  migrated: "Migrated",
  rolled_back: "Rolled back",
  failed: "Failed",
};

/**
 * Admin: move a project's own deploy identity from subscription-wide grants to
 * grants on its own resource groups (or roll that back). Opening it runs a dry
 * run, which changes nothing; so does every change of the options. The change
 * itself needs the repository name typed out. The backend decides everything --
 * this only shows its report.
 */
export function DeployIdentityScopeDialog({ project }: { project: ProjectSummary }) {
  const [open, setOpen] = useState(false);
  const [force, setForce] = useState(false);
  const [adopt, setAdopt] = useState<Environment[]>([]);
  const [report, setReport] = useState<DeployIdentityScopeMigrationResult | null>(null);
  const run = useRunDeployIdentityScopeMigration(project.id);
  const rollback = project.deploy_identity_scope === "resource_group";

  // A dry run (reads only) whenever the dialog opens or an option changes.
  const check = (options: { force: boolean; adopt: Environment[] }) => {
    setReport(null);
    run.mutate(
      { dryRun: true, rollback, ...options },
      {
        onSuccess: (data) => setReport(data),
        onError: (err) => {
          logger.error("[deploy-identity] scope dry run failed:", err);
          toast.error(readApiErrorMessage(err, "Could not check the project. Please try again."));
        },
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    // Never while a run is going: its report (what was put back, or not) must be seen.
    if (!next && run.isPending) {
      return;
    }
    setOpen(next);
    if (next) {
      setForce(false);
      setAdopt([]);
      check({ force: false, adopt: [] });
    }
  };

  const handleOptions = (options: { force: boolean; adopt: Environment[] }) => {
    setForce(options.force);
    setAdopt(options.adopt);
    check(options);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="text-muted-foreground h-auto px-1.5 py-0.5 text-xs"
        >
          {rollback ? "Resource-group scoped…" : "Scope to resource groups…"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        {open && (
          <ScopeMigrationForm
            project={project}
            rollback={rollback}
            force={force}
            adopt={adopt}
            onOptions={handleOptions}
            report={report}
            onReport={setReport}
            run={run}
            onClose={() => handleOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ScopeMigrationForm({
  project,
  rollback,
  force,
  adopt,
  onOptions,
  report,
  onReport,
  run,
  onClose,
}: {
  project: ProjectSummary;
  rollback: boolean;
  force: boolean;
  adopt: Environment[];
  onOptions: (options: { force: boolean; adopt: Environment[] }) => void;
  report: DeployIdentityScopeMigrationResult | null;
  onReport: (report: DeployIdentityScopeMigrationResult | null) => void;
  run: ScopeRun;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState("");
  const issues = report ? [...report.blockers, ...report.warnings] : [];
  const forceable = issues.some((issue) => issue.override === "force");
  const adoptable = [
    ...new Set(
      issues
        .filter((issue) => issue.override === "adopt" && issue.environment)
        .map((issue) => issue.environment as Environment),
    ),
  ];
  const canApply =
    report?.dry_run === true &&
    report.state === "ready" &&
    typed.trim() === report.confirm_name &&
    !run.isPending;

  const toggleAdopt = (environment: Environment, on: boolean) => {
    const next = on ? [...adopt, environment] : adopt.filter((env) => env !== environment);
    onOptions({ force, adopt: next });
  };

  const handleApply = () => {
    if (!canApply) {
      return;
    }
    run.mutate(
      { dryRun: false, force, adopt, rollback, confirm: typed.trim() },
      {
        onSuccess: (data) => {
          onReport(data);
          if (data?.state === "migrated" || data?.state === "rolled_back") {
            toast.success(
              `${project.project_name}: ${STATE_LABEL[data.state].toLowerCase()}. The next deploy may need a re-run while the grants propagate.`,
            );
          } else if (data) {
            toast.warning(`${project.project_name}: ${STATE_LABEL[data.state].toLowerCase()}.`);
          }
        },
        onError: (err) => {
          logger.error("[deploy-identity] scope migration failed:", err);
          toast.error(readApiErrorMessage(err, "The change did not run. Please try again."));
        },
      },
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {rollback
            ? "Roll back to subscription-wide deploy access?"
            : "Scope the deploy identity to its resource groups?"}
        </DialogTitle>
        <DialogDescription>
          {rollback
            ? "Grants Contributor on each entitled environment's subscription and Storage Blob Data Contributor on the state account again. The resource-group grants stay."
            : "Grants Contributor on each environment's own resource group, reads it back, then removes Contributor on the subscriptions and Storage Blob Data Contributor on the state account. Anything removed is put back if a later step fails."}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        {!report ? (
          <p className="text-muted-foreground text-sm">Checking (dry run, nothing changes)…</p>
        ) : (
          <ScopeMigrationReport report={report} />
        )}
        <div className="mt-4 space-y-3 border-t pt-3">
          {(forceable || force) && (
            <OptionCheckbox
              id="scope-force"
              checked={force}
              disabled={run.isPending}
              onChange={(on) => onOptions({ force: on, adopt })}
              label={
                rollback
                  ? "Force: grant subscription-wide access although this project was never migrated"
                  : "Force: go on although a branch's template cannot be confirmed"
              }
            />
          )}
          {[...new Set([...adoptable, ...adopt])].map((environment) => (
            <OptionCheckbox
              key={environment}
              id={`scope-adopt-${environment}`}
              checked={adopt.includes(environment)}
              disabled={run.isPending}
              onChange={(on) => toggleAdopt(environment, on)}
              label={`Adopt ${environment}: I know its resource group and state blob are this project's`}
            />
          ))}
          {report && (
            <div className="space-y-1.5">
              <Label htmlFor="scope-confirm">
                Type <code className="bg-muted rounded px-1 font-mono">{report.confirm_name}</code>{" "}
                to confirm
              </Label>
              <Input
                id="scope-confirm"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={run.isPending}>
          Close
        </Button>
        <Button variant="destructive" disabled={!canApply} onClick={handleApply}>
          {run.isPending ? "Working…" : rollback ? "Roll back" : "Migrate"}
        </Button>
      </DialogFooter>
    </>
  );
}

function OptionCheckbox({
  id,
  checked,
  disabled,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onChange(value === true)}
        disabled={disabled}
      />
      <Label htmlFor={id} className="text-sm font-normal">
        {label}
      </Label>
    </div>
  );
}

function ScopeMigrationReport({ report }: { report: DeployIdentityScopeMigrationResult }) {
  const removed = report.dry_run ? report.grants_to_remove : report.grants_removed;
  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant={
            report.state === "failed" || report.state === "blocked"
              ? "destructive"
              : report.state === "ready"
                ? "secondary"
                : "default"
          }
        >
          {STATE_LABEL[report.state]}
        </Badge>
        <span className="text-muted-foreground text-xs">
          {report.dry_run ? "Dry run" : "Applied"} · {report.scope_before} → {report.scope_after}
        </span>
      </div>
      {report.error && <p className="text-destructive text-xs">{report.error}</p>}
      <IssueList title="Blocking" issues={report.blockers} tone="text-destructive" />
      <IssueList
        title="Warnings"
        issues={report.warnings}
        tone="text-amber-700 dark:text-amber-400"
      />
      {report.environments.length > 0 && (
        <ul className="space-y-1 text-xs">
          {report.environments.map((env) => (
            <li key={env.environment}>
              <span className="font-medium">{env.environment}</span> ({env.branch}): template{" "}
              {env.template}, group {env.resource_group} {env.resource_group_state} ({env.ownership}
              ), state {env.tfstate}
              {env.adopt_state ? " (adopted)" : ""}
            </li>
          ))}
        </ul>
      )}
      {removed.length > 0 && (
        <div className="text-xs">
          <p className="font-medium">{report.dry_run ? "Removes" : "Removed"}</p>
          <ul className="text-muted-foreground font-mono break-all">
            {removed.map((grant) => (
              <li key={grant.id}>
                {grant.role} @ {grant.scope}
              </li>
            ))}
          </ul>
        </div>
      )}
      {report.grants_restored.length > 0 && (
        <p className="text-xs">Put back: {report.grants_restored.length} grant(s).</p>
      )}
      {report.grants_kept.length > 0 && (
        <div className="text-muted-foreground text-xs">
          <p className="font-medium">Left untouched</p>
          <ul className="font-mono break-all">
            {report.grants_kept.map((grant) => (
              <li key={grant.id}>
                {grant.role} @ {grant.scope}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const OVERRIDE_HINT: Record<NonNullable<ScopeMigrationIssue["override"]>, string> = {
  force: " (force lets it through)",
  adopt: " (adopt this environment to go on)",
};

function IssueList({
  title,
  issues,
  tone,
}: {
  title: string;
  issues: ScopeMigrationIssue[];
  tone: string;
}) {
  if (issues.length === 0) {
    return null;
  }
  return (
    <div className={`text-xs ${tone}`}>
      <p className="font-medium">{title}</p>
      <ul className="list-disc pl-4">
        {issues.map((issue) => (
          <li key={`${issue.code}-${issue.environment ?? ""}-${issue.message}`}>
            {issue.environment ? `${issue.environment}: ` : ""}
            {issue.message}
            {issue.override ? OVERRIDE_HINT[issue.override] : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}
